/**
 * AdminEventsTab - Paginated raw funnel events table.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  getDashboardEvents,
  type RawEvent,
} from '@/services/adminDashboardService';
import LeadDetailModal from './LeadDetailModal';

const PAGE_SIZE = 50;

const EVENT_LABELS: Record<string, string> = {
  account_created: 'Conta criada',
  login_completed: 'Login',
  onboarding_started: 'Onboarding iniciado',
  onboarding_completed: 'Onboarding completo',
  carousel_created: 'Carrossel criado',
  carousel_edited: 'Carrossel editado',
  carousel_exported: 'Carrossel exportado',
  checkout_initiated: 'Checkout iniciado',
  checkout_form_started: 'Form iniciado',
  checkout_form_completed: 'Form completo',
  payment_approved: 'Pagamento aprovado',
  payment_failed: 'Pagamento falhou',
  refund_requested: 'Reembolso',
};

const AdminEventsTab: React.FC = () => {
  const [events, setEvents] = useState<RawEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedEventMetadata, setSelectedEventMetadata] = useState<Record<string, any> | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<string | undefined>(undefined);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getDashboardEvents(page, PAGE_SIZE, selectedEvent);
      setEvents(res.events || []);
      setTotal(res.total || 0);
    } catch (err) {
      console.error('Failed to fetch events', err);
    } finally {
      setLoading(false);
    }
  }, [page, selectedEvent]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleEventFilter = (value: string) => {
    setSelectedEvent(value === '' ? undefined : value);
    setPage(1);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold text-foreground">
            Eventos Brutos ({total.toLocaleString('pt-BR')} total)
          </h3>
          <select
            value={selectedEvent || ''}
            onChange={(e) => handleEventFilter(e.target.value)}
            className="text-xs px-2 py-1 rounded bg-muted border border-border text-foreground"
          >
            <option value="">Todos os eventos</option>
            {Object.entries(EVENT_LABELS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="px-2 py-1 rounded bg-muted hover:bg-muted/80 disabled:opacity-40"
          >
            Anterior
          </button>
          <span>
            Página {page} de {totalPages || 1}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="px-2 py-1 rounded bg-muted hover:bg-muted/80 disabled:opacity-40"
          >
            Próximo
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-muted-foreground py-8 text-center">Carregando eventos...</div>
      ) : events.length === 0 ? (
        <div className="text-muted-foreground py-8 text-center">Nenhum evento encontrado.</div>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50">
                  <th className="text-left px-4 py-2 font-medium text-muted-foreground">Data/Hora</th>
                  <th className="text-left px-4 py-2 font-medium text-muted-foreground">Usuário</th>
                  <th className="text-left px-4 py-2 font-medium text-muted-foreground">Evento</th>
                  <th className="text-left px-4 py-2 font-medium text-muted-foreground">Detalhes</th>
                </tr>
              </thead>
              <tbody>
                {events.map((evt) => (
                    <tr key={evt.id} className="border-t border-border hover:bg-muted/30">
                      <td className="px-4 py-2 text-muted-foreground whitespace-nowrap">
                        {formatDate(evt.created_at)}
                      </td>
                      <td className="px-4 py-2">
                        <div className="text-foreground text-xs">{evt.email}</div>
                        <div className="text-muted-foreground font-mono text-[10px]">{evt.user_id}</div>
                      </td>
                      <td className="px-4 py-2">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                          {EVENT_LABELS[evt.event_name] || evt.event_name}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-muted-foreground">
                        <button
                          onClick={() => {
                            setSelectedUserId(evt.user_id);
                            setSelectedEventMetadata(evt.metadata);
                          }}
                          className="text-xs underline hover:text-foreground"
                        >
                          Ver dados
                        </button>
                      </td>
                    </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <LeadDetailModal
        userId={selectedUserId}
        eventMetadata={selectedEventMetadata}
        onClose={() => {
          setSelectedUserId(null);
          setSelectedEventMetadata(null);
        }}
      />
    </div>
  );
};

export default AdminEventsTab;
